import type { Agency } from '@saaga/shared/interfaces/agencies.interface'
import type { AgencyResponse } from '@saaga/shared/interfaces/agency.interface'
import { LinkIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { AgencyDetailBackButton } from '@/components/agencies/agency-detail-back-button.atom'
import { AgencyDetailDescriptionSection } from '@/components/agencies/agency-detail-description-section.atom'
import { AgencyDetailHeader } from '@/components/agencies/agency-detail-header.molecule'
import { AgencyDetailStartChatButton } from '@/components/agencies/agency-detail-start-chat-button.atom'
import { AgencyDetailVisibilityActions } from '@/components/agencies/agency-detail-visibility-actions.molecule'
import { AgencyFlowPreview } from '@/components/agencies/agency-flow/agency-flow-preview.organism'
import { AgencyRejectionAlert } from '@/components/agencies/agency-rejection-alert.atom'
import { Button } from '@/components/ui/button'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { containsMarkdown } from '@/utils/markdown-detect.util'

import type { UseAgencyVisibilityActionsReturn } from './use-agency-visibility-actions.hook'

const ShareAgencyButton = ({ onShareClick }: { onShareClick: () => void }): ReactNode => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        variant="outline"
        size="icon"
        className="agency-detail-share-button"
        onClick={onShareClick}
        aria-label="Share agency link"
      >
        <LinkIcon className="h-4 w-4" />
      </Button>
    </TooltipTrigger>
    <TooltipContent>Share agency link</TooltipContent>
  </Tooltip>
)

interface AgencyDetailToolbarProps {
  agency: Agency
  isOwnerContext: boolean
  visibilityActions: UseAgencyVisibilityActionsReturn
  onEdit: () => void
  onStartChat?: (agency: Agency) => void
  onShareClick?: () => void
  onBack: () => void
}

const AgencyDetailToolbar = ({
  agency,
  isOwnerContext,
  visibilityActions,
  onEdit,
  onStartChat,
  onShareClick,
  onBack,
}: AgencyDetailToolbarProps): ReactNode => (
  <div className="agency-detail-toolbar flex items-center justify-between px-6 pt-4 pb-0">
    <AgencyDetailBackButton onClick={onBack} />
    <div className="agency-detail-actions flex items-center gap-2">
      {onShareClick && <ShareAgencyButton onShareClick={onShareClick} />}
      <AgencyDetailStartChatButton agency={agency} onStartChat={onStartChat} />
      {isOwnerContext && (
        <AgencyDetailVisibilityActions
          visibility={agency.visibility}
          isLoading={visibilityActions.isLoading}
          onSubmitForReview={visibilityActions.handleSubmitForReview}
          onCancelReview={visibilityActions.handleCancelReview}
          onEdit={onEdit}
        />
      )}
    </div>
  </div>
)

interface AgencyDetailInfoPanelProps {
  agency: Agency
  agencyResponse: AgencyResponse
  isOwnerContext: boolean
  visibilityActions: UseAgencyVisibilityActionsReturn
  onEdit: () => void
  onStartChat?: (agency: Agency) => void
  onShareClick?: () => void
  onBack: () => void
}

const AgencyDetailInfoPanel = ({
  agency,
  agencyResponse,
  isOwnerContext,
  visibilityActions,
  onEdit,
  onStartChat,
  onShareClick,
  onBack,
}: AgencyDetailInfoPanelProps): ReactNode => (
  <ScrollArea className="h-full">
    <AgencyDetailToolbar
      agency={agency}
      isOwnerContext={isOwnerContext}
      visibilityActions={visibilityActions}
      onEdit={onEdit}
      onStartChat={onStartChat}
      onShareClick={onShareClick}
      onBack={onBack}
    />
    <div className="agency-detail-info space-y-6 px-6 pb-6 pt-2">
      <AgencyDetailHeader agency={agency} isVisibilityBadgeVisible={isOwnerContext} />
      {agency.visibility === 'rejected' && agency.rejectionReason && (
        <AgencyRejectionAlert rejectionReason={agency.rejectionReason} />
      )}
      {agencyResponse.presentation_description_short && (
        <AgencyDetailDescriptionSection
          title="Description"
          content={agencyResponse.presentation_description_short}
          isMarkdown={containsMarkdown(agencyResponse.presentation_description_short)}
        />
      )}
      {agencyResponse.presentation_description_who_is_it_best_for && (
        <AgencyDetailDescriptionSection
          title="Best For"
          content={agencyResponse.presentation_description_who_is_it_best_for}
          isMarkdown={containsMarkdown(agencyResponse.presentation_description_who_is_it_best_for)}
        />
      )}
      {agencyResponse.presentation_description_detailed && (
        <AgencyDetailDescriptionSection
          title="About"
          content={agencyResponse.presentation_description_detailed}
          isMarkdown={containsMarkdown(agencyResponse.presentation_description_detailed)}
        />
      )}
    </div>
  </ScrollArea>
)

interface AgencyDetailPanelsViewProps {
  agency: Agency
  agencyResponse: AgencyResponse
  isOwnerContext: boolean
  visibilityActions: UseAgencyVisibilityActionsReturn
  onEdit: () => void
  onStartChat?: (agency: Agency) => void
  onShareClick?: () => void
  onBack: () => void
}

export const AgencyDetailPanelsView = ({
  agency,
  agencyResponse,
  isOwnerContext,
  visibilityActions,
  onEdit,
  onStartChat,
  onShareClick,
  onBack,
}: AgencyDetailPanelsViewProps): ReactNode => {
  const hasGraph = agencyResponse.graph_json.nodeList.length > 0

  return (
    <ResizablePanelGroup className="agency-detail-panels flex-1">
      <ResizablePanel
        defaultSize={50}
        minSize={30}
        className="agency-detail-info-panel overflow-hidden"
      >
        <AgencyDetailInfoPanel
          agency={agency}
          agencyResponse={agencyResponse}
          isOwnerContext={isOwnerContext}
          visibilityActions={visibilityActions}
          onEdit={onEdit}
          onStartChat={onStartChat}
          onShareClick={onShareClick}
          onBack={onBack}
        />
      </ResizablePanel>
      {hasGraph && (
        <>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={50} minSize={30} className="agency-detail-graph-panel">
            <AgencyFlowPreview
              nodeList={agencyResponse.graph_json.nodeList}
              edgeList={agencyResponse.graph_json.edgeList}
            />
          </ResizablePanel>
        </>
      )}
    </ResizablePanelGroup>
  )
}
